import { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { Button, Modal } from '../ui';
import { ContactLink } from './ContactLink';
import { copyToClipboard } from '../../lib/clipboard';
import { buildMailto, buildReport, firstLineEnd } from '../../lib/report';
import { track } from '../../lib/track';
import { contacts } from '../../whats-new/preface';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  role?: string | null;
};

/** The form is mounted on open only, so the context (page, date) is the one of the moment. */
function ReportForm({ role }: { role?: string | null }) {
  const [text, setText] = useState(() =>
    buildReport({ url: window.location.href, version: __APP_VERSION__, role, userAgent: navigator.userAgent }),
  );
  const [copied, setCopied] = useState<boolean | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    area.focus();
    const end = firstLineEnd(area.value);
    area.setSelectionRange(end, end);
  }, []);

  const copy = async () => {
    const ok = await copyToClipboard(text);
    setCopied(ok);
    if (!ok) areaRef.current?.select();
    track('report-problem', 'copy', 'sidebar', { ok });
  };

  const [lead, second] = contacts.owners;

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">
        Quelque chose ne marche pas, ou pas comme prévu ? Décrivez-le en deux lignes : le contexte est déjà rempli.
      </p>
      <textarea
        ref={areaRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setCopied(null);
        }}
        rows={11}
        aria-label="Message à envoyer"
        className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm leading-relaxed shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <Button type="button" onClick={copy} leftIcon={<Copy className="w-4 h-4" />}>
          Copier le message
        </Button>
        <a
          href={buildMailto(text, contacts.owners, contacts.sponsor)}
          onClick={() => track('report-problem', 'mail', 'sidebar')}
          className="text-sm font-medium text-primary-700 underline decoration-primary-300 underline-offset-2 hover:text-primary-900"
        >
          Ou ouvrir un e-mail
        </a>
      </div>
      {copied !== null && (
        <p role="status" className={`text-sm ${copied ? 'text-neutral-700' : 'text-error-700'}`}>
          {copied ? (
            <>
              Copié. Collez-le dans Teams ou un e-mail à <ContactLink {...lead} /> ou <ContactLink {...second} />.
            </>
          ) : (
            'Copie impossible : sélectionnez le texte et copiez-le à la main.'
          )}
        </p>
      )}
      <p className="text-xs text-neutral-500">
        À l'équipe de l'app : {contacts.owners.map((o) => o.name).join(', ')} ({contacts.sponsor.name} en copie).
      </p>
    </div>
  );
}

export function ReportProblemModal({ isOpen, onClose, role }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Signaler un souci" size="md">
      <ReportForm role={role} />
    </Modal>
  );
}
